import { Button, Separator, useSidebar } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { ProgressBar } from '@tremor/react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, CircleAlert } from 'lucide-react'
import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { UsageItem, useUsageItems } from '~/hooks/usage/useUsageItems'

type NavUsageProps = {
  workspaceId: string
}

export function NavUsages({ workspaceId }: NavUsageProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { state: sidebarState } = useSidebar()
  const usages = useUsageItems(workspaceId)
  useClickAway(buttonRef, () => setIsExpanded(false))

  const [isExpanded, setIsExpanded] = useState(false)

  const hasHighUsage = usages.some((usage) => usage.percentage > 60)
  // If there's high usage, show the highest usage. Otherwise show ai usage
  const mainUsage = hasHighUsage
    ? [...usages].sort((a, b) => b.percentage - a.percentage)[0]
    : usages.find((usage) => usage.type === 'ai_spend')
  const additionalUsages = usages.filter((usage) => usage.type !== mainUsage?.type)

  if (!mainUsage || sidebarState === 'collapsed') {
    return null
  }

  return (
    <button
      ref={buttonRef}
      onClick={() => setIsExpanded((prev) => !prev)}
      className="relative m-1 flex flex-col gap-4 rounded-lg border border-gray-3 bg-background p-3 transition-all hover:border-gray-6 hover:shadow-sm active:opacity-80"
    >
      <motion.div
        className="absolute right-2 top-2 h-6 w-6"
        animate={{ rotate: isExpanded ? 0 : 180 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        <ChevronDown />
      </motion.div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="flex flex-col gap-4 overflow-hidden"
          >
            <p className="text-start text-xs text-muted-foreground">Usage</p>
            {additionalUsages.map((usage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.15, delay: index * 0.05, ease: 'easeInOut' },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.1, ease: 'easeInOut' },
                }}
              >
                <AdditionalUsage usage={usage} />
              </motion.div>
            ))}
            <Separator />
          </motion.div>
        )}
      </AnimatePresence>
      {mainUsage && <MainUsage usage={mainUsage} />}
      {hasHighUsage && (
        <Button asChild variant="secondary">
          <Link to="/workspaces/$workspaceId/usage" params={{ workspaceId }} onClick={(e) => e.stopPropagation()}>
            Manage
          </Link>
        </Button>
      )}
    </button>
  )
}

function MainUsage({ usage }: { usage: Required<UsageItem> }) {
  return (
    <div className="flex flex-col gap-3 truncate">
      <div className="flex flex-row items-center gap-1">
        <usage.icon size={16} />
        <p className="text-sm font-medium">{usage.label}</p>
      </div>
      <ProgressBar
        value={usage.percentage}
        color={usage.percentage >= 80 ? 'red' : usage.percentage >= 60 ? 'yellow' : 'blue'}
      />
      {usage.percentage < 100 && <p className="text-end text-sm text-muted-foreground">{usage.description}</p>}
      {usage.percentage >= 100 && (
        <div className="flex flex-row items-center justify-end gap-1">
          <p className="text-sm text-danger">Usage limit reached</p>
          <CircleAlert size={16} className="text-danger" />
        </div>
      )}
    </div>
  )
}

function AdditionalUsage({ usage }: { usage: UsageItem }) {
  const compactDescription = usage.description.replace(' of ', '/').replace(' used', '')

  return (
    <div className="group/usage flex flex-col gap-3 truncate">
      <div className="flex flex-row items-center justify-between">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-1">
          <usage.icon size={16} className="flex-shrink-0" />
          <p className="truncate text-sm font-medium">{usage.label}</p>
        </div>
        <div className="ml-2 flex-shrink-0 text-sm text-muted-foreground">
          <p className="group-hover/usage:hidden">{usage.percentage.toFixed(0)}%</p>
          <p className="hidden group-hover/usage:block">{compactDescription}</p>
        </div>
      </div>
      <ProgressBar
        value={usage.percentage}
        color={usage.percentage >= 80 ? 'red' : usage.percentage >= 60 ? 'yellow' : 'blue'}
      />
    </div>
  )
}
